import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useMatch, useNavigate, useParams, useLocation } from "react-router-dom";
import { useNodesState, useEdgesState } from "reactflow";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Save, ShieldCheck, BarChart3, Settings as Cog, Users, Workflow, Play, Pause, Copy, Trash2, History, Sparkles, Pencil, Eye } from 'lucide-react';
import { outreachApi } from "@/lib/api";
import {
  assertLeadNotificationConfigured,
  isQualifiedLeadStatus,
  leadStatusErrorMessage,
  toastLeadStatusUpdate,
  leadStatusLabel,
} from "@/lib/leadStatus";
import {
  CAMPAIGN_NAME_MAX_LEN,
  displayCampaignName,
  validateCampaignName,
} from "@/lib/campaignName";

import {
  campaignEditorHasUnsavedChanges,
  snapshotCampaignName,
  snapshotWorkflow,
} from "@/lib/campaignEditorUnsaved";
import {
  MESSAGE_STEP_TYPES,
  PERSONALIZATION_VARS,
  statusLabel,
  statusClass,
  stripWorkflowUiState,
  formatWorkflowInvalidError,
  removeWorkflowNode,
  formatWaitDuration,
  waitLabelFromDuration,
  normalizeWaitDurationFields,
  findIncompleteMessageSteps,
  findMissingGoalNode,
  findDisconnectedWorkflowSteps,
  findWorkflowCycleIssues,
  markIncompleteMessageSteps,
  previewPersonalizedMessage,
} from "@/lib/campaignWorkflow";
import { formatChannelConflictWarning } from "@/pages/outbound/campaignDashboard/campaignDashboardUtils";
import { CampaignActivityPanel } from "@/pages/outbound/CampaignActivityPanel";
import { activityRangeSince } from "@/lib/campaignActivity";
import { WorkflowCanvas } from "@/pages/outbound/WorkflowCanvas";
import { WorkflowPalette } from "@/pages/outbound/WorkflowPalette";
import { PacingSafetyTab } from "@/pages/outbound/PacingSafetyTab";
import { AskAgentSequenceModal } from "@/pages/outbound/AskAgentSequenceModal";
import { FilterSelect } from "@/components/common/FilterSelect";
import { useAssistantName } from "@/hooks/useAssistantName";

function _findAndSelectNode(nodeId, nodes, setSelectedNode) {
  if (!nodeId) return;
  const node = nodes.find((n) => n.id === nodeId);
  if (node) setSelectedNode(node);
}

function _validatePublishableState(nodes, edges, focusIncompleteStep) {
  const issues = findIncompleteMessageSteps(nodes);
  if (issues.length) {
    focusIncompleteStep(issues);
    toast.error(issues[0].message);
    return false;
  }
  return _checkWorkflowTopology(nodes, edges, focusIncompleteStep);
}

function _handleSaveWorkflowError(e, handleWorkflowValidationError) {
  if (e?.code === "workflow_invalid") {
    handleWorkflowValidationError(e);
  } else {
    toast.error(e.message || "Failed to save");
  }
}

function _getUnsavedChangesMessage(status) {
  const action = status === "paused" ? "resuming" : "publishing";
  return `You have unsaved changes. Please save your workflow before ${action}.`;
}

function _hasMeaningfulWorkflow(nodes) {
  const real = (nodes || []).filter((n) => (n.type || "").toLowerCase() !== "start");
  return real.length > 0;
}

function _buildWaitDurationUpdateData(selectedNode, patch) {
  const normalized = normalizeWaitDurationFields(selectedNode.data || {}, patch);
  const { resetFromZero, ...duration } = normalized;
  if (resetFromZero) {
    toast.message("Wait duration can’t be zero — reset to 2 days.");
  }
  const nextData = { ...selectedNode.data, ...duration };
  const autoLabel = waitLabelFromDuration(nextData);
  const prevAuto = waitLabelFromDuration(selectedNode.data || {});
  const keepCustomLabel = selectedNode.data?.label
    && selectedNode.data.label !== prevAuto
    && selectedNode.data.label !== "Wait";
  return {
    ...duration,
    label: keepCustomLabel ? selectedNode.data.label : autoLabel,
  };
}

function _checkWorkflowTopology(nodes, edges, focusIncompleteStep) {
  const missingGoal = findMissingGoalNode(nodes);
  if (missingGoal.length) {
    focusIncompleteStep(missingGoal);
    toast.error(missingGoal[0].message);
    return false;
  }
  const disconnected = findDisconnectedWorkflowSteps(nodes, edges);
  if (disconnected.length) {
    focusIncompleteStep(disconnected);
    toast.error(disconnected[0].message);
    return false;
  }
  const cycleIssues = findWorkflowCycleIssues(nodes, edges);
  if (cycleIssues.length) {
    focusIncompleteStep(cycleIssues);
    toast.error(cycleIssues[0].message);
    return false;
  }
  return true;
}

function EnrollmentsTable({ enrollments, handleExitEnrollment, handleSimulateReply, handleMature, enrollmentsLoading }) {
  if (enrollmentsLoading) {
    return <Skeleton className="h-24 w-full" />;
  }
  if (enrollments.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/50 p-6 sm:p-8 text-center text-[13px] text-slate-500">
        No RNCs enrolled yet. Prefer Enroll from RNC DB (live `bnc.projects`), or use a test stub.
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-slate-200 overflow-x-auto scrollbar-thin">
      <table className="w-full min-w-[720px] text-[12px]">
        <thead className="bg-slate-50 text-slate-500 text-left">
          <tr>
            <th className="px-3 py-2 font-medium">RNC</th>
            <th className="px-3 py-2 font-medium">Channel</th>
            <th className="px-3 py-2 font-medium">Status</th>
            <th className="px-3 py-2 font-medium">Lead / Step</th>
            <th className="px-3 py-2 font-medium">Next run</th>
            <th className="px-3 py-2 font-medium">Actions</th>
          </tr>
        </thead>
        <tbody>
          {enrollments.map((row) => (
            <tr key={row.id} className="border-t border-slate-100">
              <td className="px-3 py-2">
                <div className="font-medium text-slate-800">{row.display_name || row.rncId || "—"}</div>
                <div className="text-slate-400">{row.rncId || row.recipient}</div>
              </td>
              <td className="px-3 py-2 capitalize">{row.channel || "—"}</td>
              <td className="px-3 py-2">{row.status}</td>
              <td className="px-3 py-2">
                {row.leadStatus || "new"}
                {row.isStub ? " · stub" : ""}
                <div className="text-slate-400">{row.currentStepLabel || "—"}</div>
              </td>
              <td className="px-3 py-2 text-slate-500">
                {row.next_run_at ? new Date(row.next_run_at).toLocaleString() : "—"}
              </td>
              <td className="px-3 py-2">
                <div className="flex flex-col gap-1">
                  {(row.status === "active" || row.status === "paused") && (
                    <button
                      type="button"
                      className="text-[11px] text-rose-600 hover:underline text-left"
                      onClick={() => handleExitEnrollment(row.id, row.display_name)}
                      data-testid={`exit-enrollment-${row.id}`}
                    >
                      Exit campaign
                    </button>
                  )}
                  {(row.leadStatus === "new" || row.leadStatus === "in_progress" || !row.leadStatus) && (
                    <button
                      type="button"
                      className="text-[11px] text-indigo-600 hover:underline text-left"
                      onClick={() => handleSimulateReply(row.id)}
                    >
                      Simulate reply
                    </button>
                  )}
                  {row.leadStatus === "qualified_inquiry" && (
                    <>
                      <button
                        type="button"
                        className="text-[11px] text-emerald-700 hover:underline text-left"
                        onClick={() => handleMature(row.id, "qualified_mature")}
                      >
                        Mature qualify
                      </button>
                      <button
                        type="button"
                        className="text-[11px] text-slate-600 hover:underline text-left"
                        onClick={() => handleMature(row.id, "unqualified_mature")}
                      >
                        Mature unqualify
                      </button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

EnrollmentsTable.propTypes = {
  enrollments: PropTypes.arrayOf(PropTypes.object).isRequired,
  enrollmentsLoading: PropTypes.bool.isRequired,
  handleExitEnrollment: PropTypes.func.isRequired,
  handleSimulateReply: PropTypes.func.isRequired,
  handleMature: PropTypes.func.isRequired,
};

/** Prospects + Activity stay visible so contact-level exit and audit trail are reachable. */
const SHOW_EDITOR_EXTRA_TABS = true;

async function confirmChannelConflictsIfAny(campaignId) {
  try {
    const res = await outreachApi.getCampaignSignalConflicts(campaignId);
    const conflicts = res?.conflicts || [];
    if (!conflicts.length) return true;
    return window.confirm(formatChannelConflictWarning(conflicts));
  } catch {
    // Don't block activate if conflict check fails — publish still works.
    return true;
  }
}

function messagePlaceholderForType(type) {
  switch (type) {
    case "email":
      return "Hi {{first_name}},\n\n**Quick note** about [our product](https://example.com).\n\n- Point one\n- Point two";
    case "whatsapp":
      return "Hi {{first_name}} — *quick note* about https://example.com";
    case "telegram":
      return "Enter the Telegram message that will be sent to your recipients…";
    case "sms":
      return "Enter the SMS message that will be sent to your recipients…";
    case "linkedin":
      return "Enter the LinkedIn message that will be sent to your recipients…";
    case "voice":
      return "Enter the AI call script / prompt for this step…";
    default:
      return "Enter your campaign message here…";
  }
}

function messageFieldLabelForType(type) {
  return type === "voice" ? "Call script / prompt" : "Message";
}

function CampaignEditorHeader({
  readOnly,
  name,
  setName,
  campaign,
  navigate,
  onNavigateBack,
  agentName,
  setAskOpen,
  handleDuplicate,
  handlePause,
  handleResume,
  handleDelete,
  handleSave,
  saving,
}) {
  return (
    <div className="shrink-0 px-3 sm:px-4 lg:px-6 py-3 border-b border-slate-200 bg-white sticky top-0 z-10">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={onNavigateBack}
          className="text-[12px] text-slate-500 hover:text-slate-700 flex-none"
          data-testid="seq-back"
        >
          Campaigns
        </button>
        <span className="text-slate-300 flex-none">/</span>
        {readOnly ? (
          <h1
            className="font-semibold text-[14px] text-slate-900 truncate min-w-0 flex-1 basis-32"
            title={name}
            data-testid="seq-name"
          >
            {displayCampaignName(name)}
          </h1>
        ) : (
          <div className="flex-none w-64">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={CAMPAIGN_NAME_MAX_LEN}
              placeholder="Campaign name"
              aria-label="Campaign name"
              className="h-9 w-full text-[14px] font-semibold"
              data-testid="seq-name"
            />
          </div>
        )}
        {!readOnly && (
          <div className="hidden xl:block xl:flex-1 xl:min-w-2" aria-hidden="true" />
        )}
        <span
          className={cn(
            "inline-flex items-center justify-center rounded-full px-3 h-9 text-[12px] font-medium flex-none",
            statusClass(campaign.status),
          )}
          data-testid="campaign-status-badge"
        >
          {statusLabel(campaign.status)}
        </span>
        {readOnly ? (
          <Button
            size="sm"
            className="h-9 flex-none"
            onClick={() => navigate(`/outreach/campaigns/${campaign.id}/edit`)}
            data-testid="seq-edit"
          >
            <Pencil size={12} /> Edit workflow
          </Button>
        ) : (
          <>
            <Button
              size="sm"
              className="h-9 flex-none bg-violet-600 hover:bg-violet-700 text-white"
              onClick={() => setAskOpen(true)}
              data-testid="ask-agent-design-sequence"
            >
              <Sparkles size={12} />
              Ask {agentName} to design a sequence
            </Button>
            <Button variant="outline" size="sm" className="h-9 flex-none" onClick={handleDuplicate} data-testid="campaign-duplicate">
              <Copy size={12} /> Duplicate
            </Button>
            {campaign.status === "active" ? (
              <Button variant="outline" size="sm" className="h-9 flex-none" onClick={handlePause}>
                <Pause size={12} /> Pause
              </Button>
            ) : campaign.status !== "archived" && (
              <Button variant="outline" size="sm" className="h-9 flex-none" onClick={handleResume}>
                <Play size={12} /> {campaign.status === "paused" ? "Resume" : "Publish"}
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              className="h-9 w-9 p-0 flex-none text-rose-700"
              onClick={handleDelete}
              aria-label="Delete campaign"
            >
              <Trash2 size={12} />
            </Button>
            <Button
              size="sm"
              className="h-9 flex-none"
              disabled={saving}
              onClick={handleSave}
              data-testid="seq-save"
            >
              <Save size={12} /> {saving ? "Saving…" : "Save"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

CampaignEditorHeader.propTypes = {
  readOnly: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  setName: PropTypes.func.isRequired,
  campaign: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  onNavigateBack: PropTypes.func.isRequired,
  agentName: PropTypes.string.isRequired,
  setAskOpen: PropTypes.func.isRequired,
  handleDuplicate: PropTypes.func.isRequired,
  handlePause: PropTypes.func.isRequired,
  handleResume: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
};

function MessagePreview({ showPreview, setShowPreview, selectedNode }) {
  return (
    <div>
      <Button
        size="sm"
        variant="outline"
        className="w-full h-8"
        onClick={() => setShowPreview((v) => !v)}
        data-testid="campaign-step-preview-toggle"
      >
        <Eye size={12} /> {showPreview ? "Hide preview" : "Preview message"}
      </Button>
      {showPreview && (
        <div
          className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-2.5 text-[12px] text-slate-700 whitespace-pre-wrap"
          data-testid="campaign-step-preview"
        >
          {selectedNode.type === "email" && selectedNode.data?.subject && (
            <div className="mb-2 font-medium text-slate-800">
              Subject: {previewPersonalizedMessage(selectedNode.data.subject)}
            </div>
          )}
          {previewPersonalizedMessage(selectedNode.data?.message || "") || (
            <span className="text-slate-400">Nothing to preview yet.</span>
          )}
        </div>
      )}
    </div>
  );
}
MessagePreview.propTypes = {
  showPreview: PropTypes.bool.isRequired,
  setShowPreview: PropTypes.func.isRequired,
  selectedNode: PropTypes.object.isRequired,
};

function MessageTestSend({ testRecipient, setTestRecipient, testingSend, handleTestSend, selectedNode }) {
  return (
    <div className="pt-1 border-t border-slate-100 space-y-2">
      <Label htmlFor="campaign-test-recipient" className="text-[11px]">Test send recipient</Label>
      <Input
        id="campaign-test-recipient"
        className="h-10 sm:h-8 w-full min-w-0"
        value={testRecipient}
        placeholder={selectedNode.type === "email" ? "you@example.com" : "Recipient address"}
        onChange={(e) => setTestRecipient(e.target.value)}
        data-testid="campaign-test-recipient"
      />
      <Button
        size="sm"
        variant="outline"
        className="w-full h-10 sm:h-8"
        disabled={testingSend || selectedNode.type === "linkedin" || selectedNode.type === "voice"}
        onClick={handleTestSend}
        data-testid="campaign-test-send"
      >
        {testingSend ? "Sending…" : "Send test"}
      </Button>
    </div>
  );
}
MessageTestSend.propTypes = {
  testRecipient: PropTypes.string.isRequired,
  setTestRecipient: PropTypes.func.isRequired,
  testingSend: PropTypes.bool.isRequired,
  handleTestSend: PropTypes.func.isRequired,
  selectedNode: PropTypes.object.isRequired,
};

function MessageStepInspector({
  selectedNode,
  templates,
  templatesLoading,
  applyTemplate,
  updateSelectedNodeData,
  insertPersonalizationVar,
  showPreview,
  setShowPreview,
  testRecipient,
  setTestRecipient,
  testingSend,
  handleTestSend,
}) {
  const agentName = useAssistantName();
  return (
    <div className="mt-3 space-y-3">
      {(selectedNode.type === "linkedin" || selectedNode.type === "voice") && (
        <p className="text-[10.5px] text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-2 py-1.5">
          {selectedNode.type === "voice"
            ? "AI phone calls are not enabled for sending yet — configure the script now for when calling ships."
            : "LinkedIn sending is not enabled yet — configure the message now for when LinkedIn ships."}
        </p>
      )}

      {templates.length > 0 && (
        <div>
          <Label htmlFor="campaign-step-template" className="text-[11px]">Message template</Label>
          <FilterSelect
            id="campaign-step-template"
            variant="field"
            className="mt-1"
            value=""
            disabled={templatesLoading}
            onChange={(tplId) => {
              if (tplId) applyTemplate(tplId);
            }}
            options={[
              {
                value: "",
                label: templatesLoading ? "Loading templates…" : "Select a template…",
              },
              ...templates.map((tpl) => ({
                value: tpl.id,
                label: tpl.name || "Untitled",
              })),
            ]}
            data-testid="campaign-step-template"
            aria-label="Message template"
          />
        </div>
      )}

      {selectedNode.type === "email" && (
        <div>
          <Label htmlFor="campaign-step-subject" className="text-[11px]">Subject</Label>
          <Input
            id="campaign-step-subject"
            className="mt-1 h-10 sm:h-8 w-full min-w-0"
            value={selectedNode.data?.subject || ""}
            placeholder="Enter the email subject line…"
            onChange={(e) => updateSelectedNodeData({ subject: e.target.value })}
            data-testid="campaign-step-subject"
          />
          {!String(selectedNode.data?.subject || "").trim() && (
            <p className="mt-1 text-[10.5px] text-amber-700">Subject is required to activate.</p>
          )}
        </div>
      )}
      <div>
        <Label htmlFor="campaign-step-message" className="text-[11px]">
          {messageFieldLabelForType(selectedNode.type)}
        </Label>
        <Textarea
          id="campaign-step-message"
          className="mt-1 min-h-[96px] w-full min-w-0"
          value={selectedNode.data?.message || ""}
          placeholder={messagePlaceholderForType(selectedNode.type)}
          onChange={(e) => updateSelectedNodeData({ message: e.target.value })}
          data-testid="campaign-step-message"
        />
        {!String(selectedNode.data?.message || selectedNode.data?.body || selectedNode.data?.body_text || "").trim() && (
          <p className="mt-1 text-[10.5px] text-amber-700">
            {messageFieldLabelForType(selectedNode.type)} is required to activate.
          </p>
        )}
        {selectedNode.type === "email" && (
          <p className="mt-1 text-[10.5px] text-slate-500">
            Supports rich text: **bold**, *italic*, __underline__, lists, and [links](https://…).
          </p>
        )}
        {selectedNode.type === "whatsapp" && (
          <p className="mt-1 text-[10.5px] text-slate-500">
            WhatsApp formatting: *bold*, _italic_, ~strike~, ```code```, and clickable https:// links.
          </p>
        )}
      </div>

      <div>
        <div className="text-[10.5px] text-slate-500 mb-1">Personalization</div>
        <div className="flex flex-wrap gap-1">
          {PERSONALIZATION_VARS.map((v) => (
            <button
              key={v.key}
              type="button"
              className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] text-slate-600 hover:border-indigo-300 hover:bg-indigo-50"
              onClick={() => insertPersonalizationVar(v.key)}
              data-testid={`campaign-var-${v.key}`}
            >
              {`{{${v.key}}}`}
            </button>
          ))}
        </div>
      </div>

      <MessagePreview showPreview={showPreview} setShowPreview={setShowPreview} selectedNode={selectedNode} />

      <div>
        <Label htmlFor="campaign-step-ai" className="text-[11px]">AI instructions</Label>
        <Textarea
          id="campaign-step-ai"
          className="mt-1 min-h-[80px] w-full min-w-0"
          value={selectedNode.data?.ai_instructions || ""}
          placeholder={`Describe how ${agentName} should generate or personalize this message…`}
          onChange={(e) => updateSelectedNodeData({ ai_instructions: e.target.value })}
          data-testid="campaign-step-ai-instructions"
        />
      </div>
      <MessageTestSend
        testRecipient={testRecipient}
        setTestRecipient={setTestRecipient}
        testingSend={testingSend}
        handleTestSend={handleTestSend}
        selectedNode={selectedNode}
      />
    </div>
  );
}

MessageStepInspector.propTypes = {
  selectedNode: PropTypes.object.isRequired,
  templates: PropTypes.arrayOf(PropTypes.object).isRequired,
  templatesLoading: PropTypes.bool.isRequired,
  applyTemplate: PropTypes.func.isRequired,
  updateSelectedNodeData: PropTypes.func.isRequired,
  insertPersonalizationVar: PropTypes.func.isRequired,
  showPreview: PropTypes.bool.isRequired,
  setShowPreview: PropTypes.func.isRequired,
  testRecipient: PropTypes.string.isRequired,
  setTestRecipient: PropTypes.func.isRequired,
  testingSend: PropTypes.bool.isRequired,
  handleTestSend: PropTypes.func.isRequired,
};

function WaitStepInspector({
  selectedNode,
  updateSelectedWaitDuration,
  updateSelectedNodeData,
}) {
  return (
    <div className="mt-3 space-y-3">
      <div>
        <div className="text-[11px] font-medium text-slate-600 mb-1.5">Wait duration</div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label htmlFor="campaign-step-wait-days" className="text-[10px] text-slate-500">Days</Label>
            <Input
              id="campaign-step-wait-days"
              type="number"
              min={0}
              max={365}
              className="mt-1 h-9 w-full min-w-0"
              value={Number(selectedNode.data?.wait_days) || 0}
              onChange={(e) => updateSelectedWaitDuration({
                wait_days: Number(e.target.value),
              })}
              data-testid="campaign-step-wait-days"
            />
          </div>
          <div>
            <Label htmlFor="campaign-step-wait-hours" className="text-[10px] text-slate-500">Hours</Label>
            <Input
              id="campaign-step-wait-hours"
              type="number"
              min={0}
              max={23}
              className="mt-1 h-9 w-full min-w-0"
              value={Number(selectedNode.data?.wait_hours) || 0}
              onChange={(e) => updateSelectedWaitDuration({
                wait_hours: Number(e.target.value),
              })}
              data-testid="campaign-step-wait-hours"
            />
          </div>
          <div>
            <Label htmlFor="campaign-step-wait-minutes" className="text-[10px] text-slate-500">Minutes</Label>
            <Input
              id="campaign-step-wait-minutes"
              type="number"
              min={0}
              max={59}
              className="mt-1 h-9 w-full min-w-0"
              value={Number(selectedNode.data?.wait_minutes) || 0}
              onChange={(e) => updateSelectedWaitDuration({
                wait_minutes: Number(e.target.value),
              })}
              data-testid="campaign-step-wait-minutes"
            />
          </div>
        </div>
        <p className="mt-1.5 text-[10.5px] text-slate-500">
          Campaign will pause for {formatWaitDuration(selectedNode.data)} before the next step.
        </p>
      </div>
      <div>
        <Label htmlFor="campaign-step-wait-notes" className="text-[11px]">Wait notes</Label>
        <Textarea
          id="campaign-step-wait-notes"
          className="mt-1 min-h-[72px] w-full min-w-0"
          value={selectedNode.data?.notes || ""}
          placeholder="Optional notes (not used for scheduling)…"
          onChange={(e) => updateSelectedNodeData({ notes: e.target.value })}
        />
      </div>
    </div>
  );
}

WaitStepInspector.propTypes = {
  selectedNode: PropTypes.object.isRequired,
  updateSelectedWaitDuration: PropTypes.func.isRequired,
  updateSelectedNodeData: PropTypes.func.isRequired,
};

function ConditionStepInspector({
  selectedNode,
  updateSelectedNodeData,
}) {
  return (
    <div className="mt-3 space-y-3" data-testid="condition-step-editor">
      <div>
        <Label htmlFor="campaign-step-condition" className="text-[11px]">Condition</Label>
        <Textarea
          id="campaign-step-condition"
          className="mt-1 min-h-[72px] w-full min-w-0"
          value={selectedNode.data?.condition || ""}
          placeholder="Describe the if/else condition for this branch…"
          onChange={(e) => updateSelectedNodeData({ condition: e.target.value })}
          data-testid="condition-row-0"
        />
      </div>
      <div className="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2 text-[11px] text-slate-600 leading-relaxed">
        Connect two paths from this step on the canvas:{" "}
        <span className="font-semibold text-emerald-700">If</span>{" "}
        when the condition matches, and{" "}
        <span className="font-semibold text-slate-700">Else</span>{" "}
        when it does not.
      </div>
    </div>
  );
}

ConditionStepInspector.propTypes = {
  selectedNode: PropTypes.object.isRequired,
  updateSelectedNodeData: PropTypes.func.isRequired,
};

function CampaignStepInspector({
  selectedNode,
  canRemoveSelectedNode,
  handleRemoveSelectedNode,
  updateSelectedNodeData,
  templates,
  templatesLoading,
  applyTemplate,
  insertPersonalizationVar,
  showPreview,
  setShowPreview,
  testRecipient,
  setTestRecipient,
  testingSend,
  handleTestSend,
  updateSelectedWaitDuration,
}) {
  if (!selectedNode) {
    return (
      <div className="text-[12px] text-slate-400 leading-snug py-2">
        <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-2">
          Step details
        </div>
        Select a step on the canvas to configure message content.
      </div>
    );
  }

  return (
    <>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-500 mb-1">
            Step details
          </div>
          <div className="text-[13px] font-semibold break-words">{selectedNode.data?.label}</div>
          <div className="text-[11px] text-slate-500 capitalize">{selectedNode.type} step</div>
        </div>
        {canRemoveSelectedNode && (
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-rose-700 border-rose-200 hover:bg-rose-50 shrink-0"
            onClick={handleRemoveSelectedNode}
            data-testid="campaign-step-remove"
          >
            <Trash2 size={12} /> Remove
          </Button>
        )}
      </div>

      <Label htmlFor="campaign-step-name" className="text-[11px]">Step name</Label>
      <Input
        id="campaign-step-name"
        className="mt-1 h-10 sm:h-8 w-full min-w-0"
        value={selectedNode.data?.label || ""}
        placeholder="e.g. Intro email, Follow-up DM…"
        onChange={(e) => updateSelectedNodeData({ label: e.target.value })}
        data-testid="campaign-step-name"
      />

      {MESSAGE_STEP_TYPES.has(selectedNode.type) && (
        <MessageStepInspector
          selectedNode={selectedNode}
          templates={templates}
          templatesLoading={templatesLoading}
          applyTemplate={applyTemplate}
          updateSelectedNodeData={updateSelectedNodeData}
          insertPersonalizationVar={insertPersonalizationVar}
          showPreview={showPreview}
          setShowPreview={setShowPreview}
          testRecipient={testRecipient}
          setTestRecipient={setTestRecipient}
          testingSend={testingSend}
          handleTestSend={handleTestSend}
        />
      )}

      {selectedNode.type === "wait" && (
        <WaitStepInspector
          selectedNode={selectedNode}
          updateSelectedWaitDuration={updateSelectedWaitDuration}
          updateSelectedNodeData={updateSelectedNodeData}
        />
      )}

      {selectedNode.type === "condition" && (
        <ConditionStepInspector
          selectedNode={selectedNode}
          updateSelectedNodeData={updateSelectedNodeData}
        />
      )}
    </>
  );
}

CampaignStepInspector.propTypes = {
  selectedNode: PropTypes.object,
  canRemoveSelectedNode: PropTypes.bool.isRequired,
  handleRemoveSelectedNode: PropTypes.func.isRequired,
  updateSelectedNodeData: PropTypes.func.isRequired,
  templates: PropTypes.arrayOf(PropTypes.object).isRequired,
  templatesLoading: PropTypes.bool.isRequired,
  applyTemplate: PropTypes.func.isRequired,
  insertPersonalizationVar: PropTypes.func.isRequired,
  showPreview: PropTypes.bool.isRequired,
  setShowPreview: PropTypes.func.isRequired,
  testRecipient: PropTypes.string.isRequired,
  setTestRecipient: PropTypes.func.isRequired,
  testingSend: PropTypes.bool.isRequired,
  handleTestSend: PropTypes.func.isRequired,
  updateSelectedWaitDuration: PropTypes.func.isRequired,
};

function shouldIgnoreDocumentClick(e) {
  if (e.defaultPrevented) return true;
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return true;
  const anchor = e.target.closest?.("a[href]");
  if (!anchor) return true;
  if (anchor.target && anchor.target !== "_self") return true;
  const href = anchor.getAttribute("href");
  if (!href || href.startsWith("#") || href.startsWith("mailto:") || href.startsWith("tel:")) return true;
  if (/^[a-z][a-z0-9+.-]*:/i.test(href)) return true;
  const url = new URL(href, window.location.origin);
  if (url.origin !== window.location.origin) return true;
  return url.pathname === window.location.pathname && url.search === window.location.search;
}

function useUnsavedChangesGuard({
  readOnly,
  hasUnsavedChanges,
  location,
  navigate,
  suppressHistoryGuardRef,
  setLeaveOpen,
  pendingLeaveRef,
  requestLeave,
}) {
  useEffect(() => {
    if (readOnly || !hasUnsavedChanges) return undefined;

    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    const pushHistoryTrap = () => {
      window.history.pushState({ __campaignEditorUnsavedTrap: true }, "", returnTo);
    };

    pushHistoryTrap();

    const onPopState = () => {
      if (suppressHistoryGuardRef.current) return;
      navigate(returnTo, { replace: true });
      pushHistoryTrap();
      pendingLeaveRef.current = () => {
        window.history.go(-2);
      };
      setLeaveOpen(true);
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [
    readOnly,
    hasUnsavedChanges,
    location.pathname,
    location.search,
    location.hash,
    navigate,
    suppressHistoryGuardRef,
    pendingLeaveRef,
    setLeaveOpen,
  ]);

  useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    const onDocumentClick = (e) => {
      if (shouldIgnoreDocumentClick(e)) return;
      const anchor = e.target.closest("a[href]");
      const href = anchor.getAttribute("href");
      const url = new URL(href, window.location.origin);
      e.preventDefault();
      e.stopPropagation();
      requestLeave(() => navigate(`${url.pathname}${url.search}${url.hash}`));
    };
    document.addEventListener("click", onDocumentClick, true);
    return () => document.removeEventListener("click", onDocumentClick, true);
  }, [hasUnsavedChanges, navigate, requestLeave]);
}

function useCampaignTemplates(selectedNode) {
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);

  useEffect(() => {
    if (!selectedNode || !MESSAGE_STEP_TYPES.has(selectedNode.type)) {
      setTemplates([]);
      return undefined;
    }
    let cancelled = false;
    setTemplatesLoading(true);
    outreachApi.listTemplates({ channel: selectedNode.type })
      .then((data) => {
        if (cancelled) return;
        setTemplates(data.templates || data.items || data || []);
      })
      .catch(() => {
        if (!cancelled) setTemplates([]);
      })
      .finally(() => {
        if (!cancelled) setTemplatesLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedNode]);

  return { templates, setTemplates, templatesLoading };
}

function promptUnqualifiedCommentIfNeeded(lead_status) {
  if (lead_status !== "unqualified_mature" && lead_status !== "unqualified_inquiry") {
    return { ok: true, comment: null };
  }
  const note = window.prompt("Comment required for unqualified status:");
  if (note == null) return { ok: false, cancelled: true };
  const comment = String(note).trim();
  if (!comment) {
    return { ok: false, error: "A comment is required when marking a lead as unqualified" };
  }
  return { ok: true, comment };
}

function buildTemplatePatch(selectedNode, tpl) {
  const patch = {
    message: tpl.body_text || tpl.body_html || selectedNode.data?.message || "",
  };
  if (selectedNode.type === "email" && tpl.subject) {
    patch.subject = tpl.subject;
  }
  if (tpl.name && !(selectedNode.data?.label || "").trim()) {
    patch.label = tpl.name;
  }
  return patch;
}

function handleResumeError(e, handleWorkflowValidationError) {
  if (e?.code === "workflow_invalid") {
    handleWorkflowValidationError(e);
  } else if (e?.code === "signal_conflict") {
    toast.error(e.message || "This signal is already assigned to another active campaign.");
  } else {
    toast.error(e.message || "Failed to update campaign");
  }
}

export function CampaignEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditRoute = Boolean(useMatch("/outreach/campaigns/:id/edit"));
  const readOnly = !isEditRoute;
  const agentName = useAssistantName();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [activeTab, setActiveTab] = useState("flow");
  const [selectedNode, setSelectedNode] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [enrollmentsLoading, setEnrollmentsLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityTotal, setActivityTotal] = useState(0);
  const [activityRange, setActivityRange] = useState("all");
  const activityRangeRef = useRef("all");
  const [askOpen, setAskOpen] = useState(false);
  const [askGenerating, setAskGenerating] = useState(false);
  const [testRecipient, setTestRecipient] = useState("");
  const [testingSend, setTestingSend] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const { templates, templatesLoading } = useCampaignTemplates(selectedNode);
  const [baseline, setBaseline] = useState(null);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const pendingLeaveRef = useRef(null);
  const suppressHistoryGuardRef = useRef(false);

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  const canvasNodes = useMemo(() => markIncompleteMessageSteps(nodes), [nodes]);
  const incompleteSteps = useMemo(() => findIncompleteMessageSteps(nodes), [nodes]);
  const hasUnsavedChanges = useMemo(
    () => campaignEditorHasUnsavedChanges({ readOnly, baseline, name, nodes, edges }),
    [readOnly, baseline, name, nodes, edges],
  );

  const markBaseline = useCallback((nextName, nextNodes, nextEdges) => {
    setBaseline({
      name: snapshotCampaignName(nextName),
      workflow: snapshotWorkflow(nextNodes, nextEdges),
    });
  }, []);

  const requestLeave = useCallback((action) => {
    if (readOnly || !hasUnsavedChanges) {
      action();
      return;
    }
    pendingLeaveRef.current = action;
    setLeaveOpen(true);
  }, [readOnly, hasUnsavedChanges]);

  const handleStayOnEditor = useCallback(() => {
    pendingLeaveRef.current = null;
    setLeaveOpen(false);
  }, []);

  const handleDiscardAndLeave = useCallback(() => {
    const action = pendingLeaveRef.current;
    pendingLeaveRef.current = null;
    setLeaveOpen(false);
    suppressHistoryGuardRef.current = true;
    markBaseline(name, nodes, edges);
    queueMicrotask(() => {
      action?.();
      queueMicrotask(() => {
        suppressHistoryGuardRef.current = false;
      });
    });
  }, [markBaseline, name, nodes, edges]);

  const loadEnrollments = useCallback(async (campaignId) => {
    if (!campaignId) return;
    setEnrollmentsLoading(true);
    try {
      const data = await outreachApi.listEnrollments(campaignId);
      setEnrollments(data.enrollments || []);
    } catch {
      setEnrollments([]);
    } finally {
      setEnrollmentsLoading(false);
    }
  }, []);

  const loadAnalytics = useCallback(async (campaignId) => {
    if (!campaignId) return;
    try {
      const data = await outreachApi.analytics(campaignId);
      setAnalytics(data);
    } catch {
      setAnalytics(null);
    }
  }, []);

  /** Range lives in a ref so refresh call sites keep the user's selection without re-creating this. */
  const loadActivity = useCallback(async (campaignId, dateRange) => {
    if (!campaignId) return;
    const range = dateRange ?? activityRangeRef.current;
    activityRangeRef.current = range;
    setActivityLoading(true);
    try {
      const data = await outreachApi.listCampaignActivity(campaignId, {
        since: activityRangeSince(range),
      });
      const events = data.events || [];
      setActivity(events);
      setActivityTotal(typeof data.total === "number" ? data.total : events.length);
    } catch {
      setActivity([]);
      setActivityTotal(0);
    } finally {
      setActivityLoading(false);
    }
  }, []);

  const handleActivityRangeChange = useCallback((range) => {
    setActivityRange(range);
    if (campaign?.id) loadActivity(campaign.id, range);
  }, [campaign?.id, loadActivity]);

  const loadCampaign = useCallback(async () => {
    if (!id || id.startsWith("seq-")) {
      navigate("/outreach/campaigns", { replace: true });
      return;
    }
    setLoading(true);
    try {
      const c = await outreachApi.getCampaign(id);
      setCampaign(c);
      setName(c.name);
      const wf = c.workflow || { nodes: [], edges: [] };
      setNodes(wf.nodes || []);
      setEdges(wf.edges || []);
      setBaseline({
        name: snapshotCampaignName(c.name),
        workflow: snapshotWorkflow(wf.nodes || [], wf.edges || []),
      });
      loadEnrollments(c.id);
      loadAnalytics(c.id);
      loadActivity(c.id);
    } catch (e) {
      toast.error(e.message || "Failed to load campaign");
      navigate("/outreach/campaigns");
    } finally {
      setLoading(false);
    }
  }, [id, navigate, setNodes, setEdges, loadEnrollments, loadAnalytics, loadActivity]);

  useEffect(() => {
    loadCampaign();
  }, [loadCampaign]);
  useUnsavedChangesGuard({
    readOnly,
    hasUnsavedChanges,
    location,
    navigate,
    suppressHistoryGuardRef,
    setLeaveOpen,
    pendingLeaveRef,
    requestLeave,
  });

  const setWorkflow = useCallback(({ nodes: n, edges: e }) => {
    setNodes(n);
    setEdges(e);
  }, [setNodes, setEdges]);

  const handleNodeDropped = useCallback((newNode) => {
    toast.success(`Added ${newNode.data?.label || "step"} — configure it in Step details`);
    setSelectedNode(newNode);
  }, []);

  const focusIncompleteStep = useCallback((issues) => {
    _findAndSelectNode(issues?.[0]?.nodeId, nodes, setSelectedNode);
  }, [nodes]);

  const handleWorkflowValidationError = useCallback((e) => {
    const nested = e?.data?.detail?.error || e?.data?.error || {};
    _findAndSelectNode(nested.errors?.[0]?.node_id, nodes, setSelectedNode);
    toast.error(formatWorkflowInvalidError(e));
  }, [nodes]);

  const updateCampaignNameIfNeeded = useCallback(async (trimmedName) => {
    if (trimmedName === campaign.name) return;
    const updated = await outreachApi.updateCampaign(campaign.id, { name: trimmedName });
    setCampaign(updated);
  }, [campaign, setCampaign]);

  const handleSaveSuccess = useCallback(async (issues, savedNodes, savedEdges) => {
    if (issues.length) {
      focusIncompleteStep(issues);
      toast.success(
        `Draft saved — ${issues.length} step${issues.length === 1 ? "" : "s"} still need message content before activation`
      );
    } else {
      toast.success("Workflow saved");
    }
    const refreshed = await outreachApi.getCampaign(campaign.id);
    setCampaign(refreshed);
    setName(refreshed.name);
    setBaseline({
      name: snapshotCampaignName(refreshed.name),
      workflow: snapshotWorkflow(savedNodes, savedEdges),
    });
  }, [campaign, focusIncompleteStep, setCampaign]);

  const publishOrResumeCampaign = useCallback(async () => {
    const updated = campaign.status === "paused"
      ? await outreachApi.resumeCampaign(campaign.id)
      : await outreachApi.publishCampaign(campaign.id);
    return updated;
  }, [campaign]);

  const handleResumeSuccess = useCallback((updated) => {
    setCampaign(updated);
    toast.success(campaign.status === "paused" ? "Campaign resumed" : "Campaign published");
  }, [campaign, setCampaign]);

  const handleSave = async () => {
    if (!campaign) return;

    const checked = validateCampaignName(name);
    if (!checked.ok) {
      toast.error(checked.error);
      return;
    }

    const issues = findIncompleteMessageSteps(nodes);
    if (!_checkWorkflowTopology(nodes, edges, focusIncompleteStep)) {
      return;
    }

    setSaving(true);
    try {
      await outreachApi.saveWorkflow(campaign.id, {
        nodes: stripWorkflowUiState(nodes),
        edges,
      });
      await updateCampaignNameIfNeeded(checked.name);
      await handleSaveSuccess(issues, nodes, edges);
    } catch (e) {
      _handleSaveWorkflowError(e, handleWorkflowValidationError);
    } finally {
      setSaving(false);
    }
  };

  const handlePause = async () => {
    try {
      const updated = await outreachApi.pauseCampaign(campaign.id);
      setCampaign(updated);
      toast.success("Campaign paused");
    } catch (e) {
      toast.error(e.message || "Failed to pause");
    }
  };

  const handleResume = async () => {
    if (hasUnsavedChanges) {
      toast.error(_getUnsavedChangesMessage(campaign.status));
      return;
    }

    if (campaign.status !== "paused" && !_validatePublishableState(nodes, edges, focusIncompleteStep)) {
      return;
    }

    try {
      const updated = await publishOrResumeCampaign();
      handleResumeSuccess(updated);
    } catch (e) {
      handleResumeError(e, handleWorkflowValidationError);
    }
  };

  const handleDuplicate = () => {
    requestLeave(async () => {
      try {
        const copy = await outreachApi.duplicateCampaign(campaign.id);
        toast.success("Campaign duplicated");
        navigate(`/outreach/campaigns/${copy.id}/edit`);
      } catch (e) {
        toast.error(e.message || "Failed to duplicate");
      }
    });
  };

  const handleEnrollStubRnc = async () => {
    if (!campaign) return;
    try {
      const result = await outreachApi.enrollRncs(campaign.id, {
        stub_id: "fake-rnc-001",
        start_immediately: campaign.status === "active",
      });
      toast.success(`Enrolled ${result.enrolled || 0} test RNC`);
      await loadEnrollments(campaign.id);
      await loadAnalytics(campaign.id);
      setActiveTab("prospects");
    } catch (e) {
      toast.error(e.message || "Failed to enroll test RNC");
    }
  };

  const handleEnrollFromRmc = async () => {
    if (!campaign) return;
    try {
      const result = await outreachApi.enrollFromRmc(campaign.id, {
        limit: 5,
        start_immediately: campaign.status === "active",
      });
      toast.success(`Enrolled ${result.enrolled || 0} RNCs from RNC DB`);
      await loadEnrollments(campaign.id);
      await loadAnalytics(campaign.id);
      setActiveTab("prospects");
    } catch (e) {
      toast.error(e.message || "Failed to enroll from RNC DB");
    }
  };

  const handleSimulateReply = async (enrollmentId) => {
    if (!campaign) return;
    try {
      await outreachApi.simulateEnrollmentReply(campaign.id, enrollmentId);
      toast.success("Reply simulated — journey closed as qualified inquiry");
      await loadEnrollments(campaign.id);
      await loadAnalytics(campaign.id);
      await loadActivity(campaign.id);
    } catch (e) {
      toast.error(e.message || "Simulate reply failed");
    }
  };

  const handleMature = async (enrollmentId, lead_status) => {
    if (!campaign) return;
    const promptRes = promptUnqualifiedCommentIfNeeded(lead_status);
    if (!promptRes.ok) {
      if (promptRes.error) toast.error(promptRes.error);
      return;
    }
    try {
      if (isQualifiedLeadStatus(lead_status)) {
        await assertLeadNotificationConfigured();
      }
      const result = await outreachApi.setEnrollmentLeadStatus(campaign.id, enrollmentId, lead_status, promptRes.comment);
      toastLeadStatusUpdate(toast, result, lead_status);
      await loadEnrollments(campaign.id);
      await loadAnalytics(campaign.id);
      await loadActivity(campaign.id);
    } catch (e) {
      toast.error(leadStatusErrorMessage(e));
    }
  };

  const handleExitEnrollment = async (enrollmentId, displayName) => {
    if (!campaign) return;
    const label = displayName || "this contact";
    if (!window.confirm(`Exit campaign for ${label}? Future steps and pending messages for this contact will be cancelled. Other enrollments continue.`)) {
      return;
    }
    try {
      const result = await outreachApi.exitEnrollment(campaign.id, enrollmentId);
      if (result?.changed === false) {
        toast.message("Contact already exited or completed");
      } else {
        toast.success(`Exited campaign for ${label}`);
      }
      await loadEnrollments(campaign.id);
      await loadAnalytics(campaign.id);
      await loadActivity(campaign.id);
    } catch (e) {
      toast.error(e.message || "Failed to exit campaign for contact");
    }
  };

  const handleTestSend = async () => {
    if (!campaign || !selectedNode) return;
    const recipient = testRecipient.trim();
    if (!recipient) {
      toast.error("Enter a test recipient");
      return;
    }
    setTestingSend(true);
    try {
      await outreachApi.testSend(campaign.id, {
        recipient,
        channel: selectedNode.type,
        node_id: selectedNode.id,
      });
      toast.success("Test send succeeded");
    } catch (e) {
      toast.error(e.message || "Test send failed");
    } finally {
      setTestingSend(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this campaign? Active enrollments will be cancelled. This cannot be undone.")) return;
    try {
      const result = await outreachApi.deleteCampaign(campaign.id);
      const cancelled = result?.enrollmentsCancelled || 0;
      toast.success(
        cancelled > 0
          ? `Campaign deleted (${cancelled} enrollment(s) cancelled)`
          : "Campaign deleted",
      );
      markBaseline(name, nodes, edges);
      navigate("/outreach/campaigns");
    } catch (e) {
      toast.error(e.message || "Failed to delete");
    }
  };

  const handleAskGenerate = async (payload) => {
    if (!campaign) return;
    if (_hasMeaningfulWorkflow(nodes) && !window.confirm("Replace the current sequence on the canvas with the generated one?")) {
      return;
    }
    setAskGenerating(true);
    try {
      const result = await outreachApi.aiDesignSequence(campaign.id, payload);
      const wf = result.workflow || { nodes: [], edges: [] };
      setNodes(wf.nodes || []);
      setEdges(wf.edges || []);
      setSelectedNode(null);
      setAskOpen(false);
      setActiveTab("flow");
      toast.success(`${agentName} drafted a sequence — review message content, then Save`);
      await loadActivity(campaign.id);
      if (result.warnings?.length) {
        toast.message(`${result.warnings.length} design warning(s)`);
      }
    } catch (e) {
      toast.error(e.message || "Failed to design sequence");
    } finally {
      setAskGenerating(false);
    }
  };

  const updateSelectedNodeData = (patch) => {
    if (!selectedNode) return;
    setNodes((nds) => nds.map((n) => (
      n.id === selectedNode.id ? { ...n, data: { ...n.data, ...patch } } : n
    )));
    setSelectedNode((n) => (n ? { ...n, data: { ...n.data, ...patch } } : n));
  };

  const insertPersonalizationVar = (key) => {
    if (!selectedNode || !MESSAGE_STEP_TYPES.has(selectedNode.type)) return;
    const token = `{{${key}}}`;
    const current = selectedNode.data?.message || "";
    updateSelectedNodeData({ message: `${current}${current && !current.endsWith(" ") ? " " : ""}${token}` });
  };

  const applyTemplate = (templateId) => {
    if (!templateId || !selectedNode) return;
    const tpl = templates.find((t) => String(t.id) === String(templateId));
    if (!tpl) return;
    const patch = buildTemplatePatch(selectedNode, tpl);
    updateSelectedNodeData(patch);
    toast.success(`Applied template “${tpl.name || "Untitled"}”`);
  };

  // Keep inspector in sync when the selected node is removed or replaced.
  useEffect(() => {
    if (!selectedNode?.id) return;
    const fresh = nodes.find((n) => n.id === selectedNode.id);
    if (!fresh) setSelectedNode(null);
  }, [nodes, selectedNode?.id]);

  const updateSelectedWaitDuration = (patch) => {
    if (!selectedNode || selectedNode.type !== "wait") return;
    updateSelectedNodeData(_buildWaitDurationUpdateData(selectedNode, patch));
  };

  const handleNodeRemoved = useCallback((removedIds) => {
    setSelectedNode((current) => (
      current && removedIds.includes(current.id) ? null : current
    ));
  }, []);

  const handleRemoveSelectedNode = () => {
    if (!selectedNode || selectedNode.id === "start" || selectedNode.type === "start") return;
    const result = removeWorkflowNode(selectedNode.id, nodes, edges);
    if (!result) return;
    setWorkflow(result);
    setSelectedNode(null);
    toast.success("Step removed");
  };

  const canRemoveSelectedNode = selectedNode
    && selectedNode.id !== "start"
    && selectedNode.type !== "start";

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[480px] w-full" />
      </div>
    );
  }

  if (!campaign) return null;

  const flowGridColsClass = readOnly
    ? "md:grid-cols-1"
    : "md:grid-cols-[180px_minmax(0,1fr)_280px]";

  let enrollmentsContent = (
    <EnrollmentsTable
      enrollments={enrollments}
      handleExitEnrollment={handleExitEnrollment}
      handleSimulateReply={handleSimulateReply}
      handleMature={handleMature}
      enrollmentsLoading={enrollmentsLoading}
    />
  );

  let activityContent = (
    <CampaignActivityPanel
      activity={activity}
      activityLoading={activityLoading}
      activityTotal={activityTotal}
      dateFilter={activityRange}
      onDateFilterChange={handleActivityRangeChange}
    />
  );

  return (
    <div
      className="h-full min-h-0 min-w-0 overflow-y-auto overflow-x-hidden scrollbar-thin"
      data-testid="campaign-editor"
    >
      <div className="flex min-h-full flex-col md:h-full md:min-h-0">
      <CampaignEditorHeader
        readOnly={readOnly}
        name={name}
        setName={setName}
        campaign={campaign}
        navigate={navigate}
        onNavigateBack={() => requestLeave(() => navigate("/outreach/campaigns"))}
        agentName={agentName}
        setAskOpen={setAskOpen}
        handleDuplicate={handleDuplicate}
        handlePause={handlePause}
        handleResume={handleResume}
        handleDelete={handleDelete}
        handleSave={handleSave}
        saving={saving}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-1 flex-col min-w-0 md:min-h-0">
        {SHOW_EDITOR_EXTRA_TABS && (
        <div className="border-b border-slate-200 bg-white px-3 sm:px-6 py-2 shrink-0">
          <TabsList className="bg-transparent p-0 h-auto min-h-9 gap-1.5 flex flex-wrap justify-start w-full">
            <TabsTrigger value="flow" className="h-9 data-[state=active]:bg-slate-100" data-testid="seq-tab-flow">
              <Workflow size={12} /> Flow
            </TabsTrigger>
            <TabsTrigger value="prospects" className="h-9 data-[state=active]:bg-slate-100" data-testid="seq-tab-prospects">
              <Users size={12} /> Prospects
            </TabsTrigger>
            <TabsTrigger value="analytics" className="h-9 data-[state=active]:bg-slate-100" data-testid="seq-tab-analytics">
              <BarChart3 size={12} /> Analytics
            </TabsTrigger>
            <TabsTrigger value="activity" className="h-9 data-[state=active]:bg-slate-100" data-testid="seq-tab-activity">
              <History size={12} /> Activity
            </TabsTrigger>
            <TabsTrigger value="pacing" className="h-9 data-[state=active]:bg-slate-100" data-testid="seq-tab-pacing">
              <ShieldCheck size={12} /> Pacing & Safety
            </TabsTrigger>
            <TabsTrigger value="settings" className="h-9 data-[state=active]:bg-slate-100" data-testid="seq-tab-settings">
              <Cog size={12} /> Settings
            </TabsTrigger>
          </TabsList>
        </div>
        )}

        <TabsContent
          value="flow"
          className="mt-0 min-w-0 flex-1 min-h-[calc(100dvh-11rem)] md:min-h-0 overflow-hidden data-[state=active]:flex data-[state=active]:flex-col"
        >
          <div className={cn(
            "flex-1 min-h-0 flex flex-col overflow-y-auto md:overflow-hidden md:grid",
            flowGridColsClass,
          )}>
            {!readOnly && (
              <WorkflowPalette className="border-b md:border-b-0 md:border-r border-slate-200 shrink-0 md:min-h-0" />
            )}
            <div className="relative h-[min(520px,55dvh)] min-h-[320px] sm:min-h-[400px] md:h-auto md:min-h-0 md:flex-1 bg-slate-50/30 overflow-hidden">
              {!readOnly && incompleteSteps.length > 0 && (
                <div
                  className="absolute top-2 left-2 right-2 z-20 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800 shadow-sm"
                  data-testid="workflow-incomplete-banner"
                >
                  {incompleteSteps.length} step{incompleteSteps.length === 1 ? "" : "s"} need message content before activation.
                  <button
                    type="button"
                    className="ml-2 font-semibold underline underline-offset-2"
                    onClick={() => focusIncompleteStep(incompleteSteps)}
                  >
                    Review
                  </button>
                </div>
              )}
              <div className="absolute inset-0">
                <WorkflowCanvas
                  nodes={canvasNodes}
                  edges={edges}
                  onNodesChange={readOnly ? undefined : onNodesChange}
                  onEdgesChange={readOnly ? undefined : onEdgesChange}
                  onWorkflowChange={readOnly ? undefined : setWorkflow}
                  onAddNode={readOnly ? undefined : handleNodeDropped}
                  onSelect={readOnly ? undefined : setSelectedNode}
                  onNodeRemoved={readOnly ? undefined : handleNodeRemoved}
                  readOnly={readOnly}
                />
              </div>
            </div>
            {!readOnly && (
              <aside
                className="border-t md:border-t-0 md:border-l border-slate-200 bg-white p-3 overflow-y-auto md:overflow-y-scroll overscroll-contain scrollbar-thin shrink-0 min-h-0 max-h-[min(48dvh,420px)] md:h-full md:max-h-none"
                data-testid="campaign-step-inspector"
              >
                <CampaignStepInspector
                  selectedNode={selectedNode}
                  canRemoveSelectedNode={canRemoveSelectedNode}
                  handleRemoveSelectedNode={handleRemoveSelectedNode}
                  updateSelectedNodeData={updateSelectedNodeData}
                  templates={templates}
                  templatesLoading={templatesLoading}
                  applyTemplate={applyTemplate}
                  insertPersonalizationVar={insertPersonalizationVar}
                  showPreview={showPreview}
                  setShowPreview={setShowPreview}
                  testRecipient={testRecipient}
                  setTestRecipient={setTestRecipient}
                  testingSend={testingSend}
                  handleTestSend={handleTestSend}
                  updateSelectedWaitDuration={updateSelectedWaitDuration}
                />
              </aside>
            )}
          </div>
        </TabsContent>

        {SHOW_EDITOR_EXTRA_TABS && (
          <>
            <TabsContent value="prospects" className="flex-1 min-h-0 mt-0 overflow-y-auto p-4 sm:p-6">
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <div className="text-[13px] text-slate-600">
                  Enrolled revenue-notable-contacts (RNCs)
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8"
                    onClick={handleEnrollFromRmc}
                    data-testid="enroll-from-rmc"
                  >
                    Enroll from RNC DB
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8"
                    onClick={handleEnrollStubRnc}
                    data-testid="enroll-test-rnc"
                  >
                    Enroll test stub
                  </Button>
                </div>
              </div>
              {enrollmentsContent}
            </TabsContent>

            <TabsContent value="analytics" className="flex-1 min-h-0 mt-0 overflow-y-auto p-4 sm:p-6">
              {!analytics ? (
                <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50/50 p-6 text-center text-[13px] text-slate-500">
                  Analytics will appear once enrollments exist.
                </div>
              ) : (
                <div className="space-y-4 max-w-3xl">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      ["Total", analytics.enrollments?.total],
                      ["Active", analytics.enrollments?.active],
                      ["Completed", analytics.enrollments?.completed],
                      ["Failed", analytics.enrollments?.failed],
                    ].map(([label, val]) => (
                      <div key={label} className="rounded-lg border border-slate-200 bg-white p-3">
                        <div className="text-[11px] text-slate-500">{label}</div>
                        <div className="text-lg font-semibold text-slate-900">{val ?? 0}</div>
                      </div>
                    ))}
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <div className="text-[12px] font-medium text-slate-700 mb-2">Lead statuses</div>
                    <div className="flex flex-wrap gap-2 text-[12px]">
                      {Object.entries(analytics.lead_statuses || {}).length === 0 ? (
                        <span className="text-slate-400">No lead status data yet</span>
                      ) : (
                        Object.entries(analytics.lead_statuses || {}).map(([k, v]) => (
                          <span key={k} className="rounded bg-slate-100 px-2 py-1 text-slate-700">
                            {leadStatusLabel(k) || k}: {v}
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white p-4">
                    <div className="text-[12px] font-medium text-slate-700 mb-2">Sends</div>
                    <div className="text-[12px] text-slate-600">
                      Sent {analytics.sends?.sent ?? 0} · Failed {analytics.sends?.failed ?? 0} · Waiting {analytics.sends?.waiting ?? 0}
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            <TabsContent value="activity" className="flex-1 min-h-0 mt-0 overflow-y-auto p-4 sm:p-6">
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="text-[13px] text-slate-600">
                  Recent Campaign Events
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8"
                  onClick={() => campaign && loadActivity(campaign.id)}
                  data-testid="refresh-activity"
                >
                  Refresh
                </Button>
              </div>
              {activityContent}
            </TabsContent>

            <TabsContent value="pacing" className="flex-1 min-h-0 mt-0 overflow-y-auto">
              <PacingSafetyTab />
            </TabsContent>

            <TabsContent value="settings" className="flex-1 min-h-0 mt-0 overflow-y-auto p-4 sm:p-6 max-w-2xl">
              <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-2 text-[12.5px] break-words">
                <div><span className="text-slate-500">Template:</span> {campaign.template || "Custom"}</div>
                <div><span className="text-slate-500">Signals:</span> {(campaign.signalSources || campaign.assignedSignals || campaign.channels || []).join(", ") || "—"}</div>
                <div><span className="text-slate-500">Created:</span> {campaign.created_at ? new Date(campaign.created_at).toLocaleString() : "—"}</div>
                <div><span className="text-slate-500">Last updated:</span> {campaign.updated_at ? new Date(campaign.updated_at).toLocaleString() : "—"}</div>
              </div>
            </TabsContent>
          </>
        )}
      </Tabs>

      {!readOnly && (
        <AskAgentSequenceModal
          open={askOpen}
          agentName={agentName}
          generating={askGenerating}
          onOpenChange={setAskOpen}
          onGenerate={handleAskGenerate}
          initialSignalSources={campaign?.signalSources || []}
        />
      )}

      <AlertDialog
        open={leaveOpen}
        onOpenChange={(open) => {
          if (open) return;
          // Escape / overlay dismiss while a leave is pending = Stay.
          // Discard clears pendingLeaveRef first, so it won't land here as Stay.
          if (pendingLeaveRef.current) handleStayOnEditor();
          else setLeaveOpen(false);
        }}
      >
        <AlertDialogContent data-testid="unsaved-changes-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave this page?
              Your edits will be discarded.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="unsaved-changes-stay"
              onClick={handleStayOnEditor}
            >
              Stay
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="unsaved-changes-discard"
              className={cn(buttonVariants({ variant: "destructive" }))}
              onClick={handleDiscardAndLeave}
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </div>
  );
}
